import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  type ReactNode,
} from 'react';

import {
  Button,
  type ButtonProps,
} from '@/core/tamagoshiTailwind/components/Button/Button';
import { IconButton } from '@/core/tamagoshiTailwind/components/IconButton/IconButton';
import {
  Overlay,
  type OverlayHandler,
} from '@/core/tamagoshiTailwind/components/Overlay/Overlay';
import { CrossIcon } from '@/core/tamagoshiTailwind/icons/CrossIcon';
import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';
import { type PrefixObjectKeys } from '@/core/tamagoshiTailwind/util/utils.type';

import {
  AnimatedDialog,
  type AnimatedRootHandler,
} from './components/AnimatedDialog';
import { closeButtonAriaLabel, Header } from './components/Header';
import { useAnimationsTracker } from './useAnimationsTracker';

type ActionProps = Pick<
  ButtonProps,
  'disabled' | 'loading' | 'onClick' | 'text'
>;

type ModalProps = {
  /** The content of the modal */
  children: ReactNode;
  /** Will be passed down to the inner `dialog` element */
  className?: string;
  /** Allow modal to display content with full width (no padding and no header) */
  layout?: 'default' | 'full';
  /** Called when the modal starts its closing animation */
  onClose?(): void;
  /**
   * Called when the modal has completed all of its closing animations.
   *
   * Use this callback in order to unmount the modal. If the modal is unmounted
   * before the animation is completed, the animation will be interrupted and
   * the modal will disappear instantly.
   */
  onCloseComplete?(): void;
  /**
   * The title of the modal.
   *
   * This will be displayed in the header of the modal on two lines at most and
   * truncated if it's too long.
   * If layout is set to `full`, the title will not be displayed but used as aria-label.
   */
  title: string;
} & PrefixObjectKeys<'primary', ActionProps> &
  PrefixObjectKeys<'secondary', ActionProps>;

/**
 * The Modal component is displayed with an overlay on top of all other content.
 *
 * It's full width on mobile and centered on desktop with a max-width.
 * Animated for the opening and closing, the modal is considered open when
 * mounted in the DOM, and the opening animation starts directly.
 *
 * Support _async_ actions together with the appropriate inherited button
 * properties. They are prefixed with either `primary` or `secondary`. To close
 * the modal programmatically, use the exposed `close` React ref handle.
 *
 * See the related [Figma][figma] for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15902-17988
 */
export const Modal = forwardRef<{ close(): void }, ModalProps>(
  (
    {
      children,
      className,
      layout = 'default',
      onClose,
      onCloseComplete,
      primaryDisabled,
      primaryLoading,
      primaryOnClick,
      primaryText,
      secondaryDisabled,
      secondaryLoading,
      secondaryOnClick,
      secondaryText,
      title,
    },
    ref,
  ) => {
    const overlayRef = useRef<OverlayHandler>(null);
    const animatedRootRef = useRef<AnimatedRootHandler>(null);
    const { areAnimationsComplete, markAnimationComplete, markOnCloseCalled } =
      useAnimationsTracker();

    const close = useCallback(
      (dismissOverlay: boolean) => {
        if (dismissOverlay) {
          overlayRef.current?.startDismissAnimation();
        }
        animatedRootRef.current?.startCloseAnimation();
        markOnCloseCalled();
        onClose?.();
      },
      [onClose, markOnCloseCalled],
    );

    useImperativeHandle(ref, () => ({ close: () => close(true) }), [close]);

    const handleAnimationComplete = (component: 'modal' | 'overlay') => {
      // NOTE To ensure the closing animation is completed, the component must
      //      be unmounted after the `onCloseComplete` callback is called.
      markAnimationComplete(component);
      if (areAnimationsComplete()) {
        onCloseComplete?.();
      }
    };

    return (
      <Overlay
        onDismiss={() => close(false)}
        onDismissComplete={() => handleAnimationComplete('overlay')}
        ref={overlayRef}
        variant="strong"
      >
        <div
          className="pointer-events-none flex h-full w-full items-end
            justify-center desktop:items-center"
        >
          <AnimatedDialog
            className={twMerge(
              'pointer-events-auto overflow-hidden',
              className,
            )}
            onCloseComplete={() => handleAnimationComplete('modal')}
            ref={animatedRootRef}
            withoutPadding={layout === 'full'}
          >
            {layout === 'default' && (
              <Header title={title} onClose={() => close(true)} />
            )}
            {layout === 'full' && (
              <div className="absolute right-m top-m" aria-label={title}>
                <IconButton
                  aria-label={closeButtonAriaLabel}
                  icon={CrossIcon}
                  onClick={() => close(true)}
                />
              </div>
            )}
            <div className="h-full overflow-auto">{children}</div>
            {(primaryText || secondaryText) && (
              <div className="flex justify-end">
                <div className="flex w-full gap-m desktop:w-auto">
                  {secondaryText && (
                    <Button
                      className="desktop:w-auto"
                      disabled={secondaryDisabled ?? secondaryLoading}
                      loading={secondaryLoading}
                      onClick={secondaryOnClick}
                      text={secondaryText}
                      variant="secondary"
                    />
                  )}
                  {primaryText && (
                    <Button
                      className="desktop:w-auto"
                      disabled={primaryDisabled ?? primaryLoading}
                      loading={primaryLoading}
                      onClick={primaryOnClick}
                      text={primaryText}
                      variant="primary"
                    />
                  )}
                </div>
              </div>
            )}
          </AnimatedDialog>
        </div>
      </Overlay>
    );
  },
);

Modal.displayName = 'Modal';
